import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, BookOpen, Scale } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACCOUNTS = ["Kas", "Bank", "Kas Kecil", "Piutang SPP", "Utang", "Modal", "Pendapatan", "Beban Gaji", "Beban Operasional", "Lainnya"];
const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ account: "Kas", debit: "", credit: "" });
const EMPTY = { entry_date: today(), description: "", lines: [emptyLine(), emptyLine()] };

export default function Journal() {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/journal").then((res) => setEntries(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setLine = (i, patch) => {
    const lines = form.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    setForm({ ...form, lines });
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, emptyLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const totalDebit = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = totalDebit === totalCredit && totalDebit > 0;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/journal", {
        ...form,
        lines: form.lines.map((l) => ({ account: l.account, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })),
      });
      toast.success("Jurnal tercatat");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan jurnal"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Hapus jurnal "${e.description}"?`)) return;
    try {
      await api.delete(`/journal/${e.id}`);
      toast.success("Jurnal dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus jurnal"));
    }
  };

  return (
    <div className="space-y-6" data-testid="journal-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Jurnal Manual</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {entries ? `${entries.length} entri jurnal` : "Memuat..."} — pencatatan debit/kredit (jurnal umum).
          </p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-journal-button">
          <Plus className="h-4 w-4" /> Tambah Jurnal
        </Button>
      </div>

      {!entries ? (
        <p className="py-16 text-center text-muted-foreground">Memuat jurnal...</p>
      ) : entries.length === 0 ? (
        <Card className="border-border shadow-sm">
          <CardContent className="py-14 text-center text-muted-foreground" data-testid="journal-empty">
            Belum ada entri jurnal. Klik "Tambah Jurnal" untuk mencatat.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {entries.map((e) => (
            <Card key={e.id} className="border-border shadow-sm" data-testid={`journal-row-${e.id}`}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between border-b border-border px-5 py-3">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-semibold">{e.description}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(e.entry_date)} • oleh {e.created_by || "Admin"}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(e)} data-testid={`delete-journal-${e.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-5 py-2 text-left font-medium">Akun</th>
                      <th className="px-5 py-2 text-right font-medium">Debit</th>
                      <th className="px-5 py-2 text-right font-medium">Kredit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {e.lines.map((l, i) => (
                      <tr key={i} className="border-b border-border/50 last:border-0 hover:bg-slate-50">
                        <td className="px-5 py-2.5">
                          <span className={l.credit > 0 ? "pl-6 inline-block" : ""}>{l.account}</span>
                        </td>
                        <td className="px-5 py-2.5 text-right font-medium">{l.debit > 0 ? rupiah(l.debit) : ""}</td>
                        <td className="px-5 py-2.5 text-right font-medium">{l.credit > 0 ? rupiah(l.credit) : ""}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-bold">
                      <td className="px-5 py-2.5 text-xs uppercase tracking-wide text-muted-foreground">Total</td>
                      <td className="px-5 py-2.5 text-right text-primary">{rupiah(e.total_debit)}</td>
                      <td className="px-5 py-2.5 text-right text-primary">{rupiah(e.total_credit)}</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="journal-form-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Entri Jurnal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Tanggal</Label>
                <DatePicker value={form.entry_date} onChange={(v) => setForm({ ...form, entry_date: v })} testId="journal-date-picker" />
              </div>
              <div className="space-y-2">
                <Label>Deskripsi *</Label>
                <Input placeholder="cth: Pembelian buku secara tunai" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="journal-description-input" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_140px_140px_36px] items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <span>Akun</span><span className="text-right">Debit</span><span className="text-right">Kredit</span><span />
              </div>
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_140px_140px_36px] items-center gap-2">
                  <Select value={l.account} onValueChange={(v) => setLine(i, { account: v })}>
                    <SelectTrigger data-testid={`journal-account-select-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ACCOUNTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" min="0" className="text-right" placeholder="0" value={l.debit}
                    onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? "" : l.credit })}
                    data-testid={`journal-debit-${i}`} />
                  <Input type="number" min="0" className="text-right" placeholder="0" value={l.credit}
                    onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? "" : l.debit })}
                    data-testid={`journal-credit-${i}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={form.lines.length <= 2}
                    onClick={() => removeLine(i)} data-testid={`journal-remove-line-${i}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5" data-testid="journal-add-line-button">
                <Plus className="h-3.5 w-3.5" /> Tambah Baris
              </Button>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border bg-slate-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm">
                <Scale className="h-4 w-4 text-primary" />
                <span>Debit <b>{rupiah(totalDebit)}</b> • Kredit <b>{rupiah(totalCredit)}</b></span>
              </div>
              <Badge
                className={balanced ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-accent/10 text-accent hover:bg-accent/10"}
                data-testid="journal-balance-status"
              >
                {balanced ? "Seimbang" : "Belum seimbang"}
              </Badge>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="journal-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving || !form.description || !balanced} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="journal-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan Jurnal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
